import React, {useEffect, useRef, useState} from 'react'
import {Box, Input, InputGroup, Text, Image, FormControl, FormLabel} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import SpecialIcons from './special-icons'
import PropTypes from 'prop-types'
import {getUrl} from '../../../utils/cloudinary-utils'
import {Symbols} from '../../icons'
import {
    formatMonogramInput,
    replaceSymbols,
    getAllIcons,
    getZodiacSigns
} from '../../../utils/engraving-utils'
import useTagManager from '../../../hooks/use-tag-manager'

// Symbols that count as double length
const symbolChars = /[\uE000-\uF8FF]/
const largeIcons =
    '\uE00B\uE00C\uE00D\uE00E\uE00F\uE010\uE011\uE012\uE013\uE014\uE015\uE016\uE017\uE018'

const allowedCharsRegex = new RegExp(
    '^[A-Za-z0-9 \\-~!“#$%&‘\'"()*+,./:;?\uE000-\uE00A' + largeIcons + ']*$'
)

const EngravingInput = ({
    id,
    value,
    maxLength,
    setDisplayIconType,
    displayIconType,
    setSelectedHeading,
    selectedHeading,
    setShowFonts,
    handleLineChange,
    product,
    showSpinner,
    restrictionListArray = '',
    selectedFont,
    selectedFaceLines
}) => {
    const tagManager = useTagManager()
    const isMonogram = selectedFont === 'Monogram'
    if (isMonogram) {
        /** Monogram font will 'always' have 3 length **/
        maxLength = 3
        value = selectedFaceLines?.['monogram'] || ''
    }
    const cleanedValue =
        typeof value === 'string'
            ? value
                  ?.replaceAll('{{#font=kendra-characters-24-01-22.ttf}}', '')
                  ?.replaceAll('{{/}}', '')
            : ''
    const inputRef = useRef()
    const [showSpecialIcons, setShowSpecialIcons] = useState(false)

    useEffect(() => {
        if (selectedHeading === 'type') {
            setShowSpecialIcons(false)
        }
    }, [selectedHeading])

    const getEngravedSymbols = (cleanedVal) => {
        const allIcons = getAllIcons()

        if (!cleanedVal) return []

        return [...cleanedVal]
            .map((c) => '\\u' + c.codePointAt(0).toString(16).padStart(4, '0'))
            .map((v) => {
                const iconObj = allIcons.find((obj) => Object.values(obj)[0] === v)
                return iconObj ? Object.keys(iconObj)[0] : null
            })
            .filter(Boolean) // remove nulls
    }

    const symbolsEngraved = getEngravedSymbols(cleanedValue)
    const [isFocused, setIsFocused] = useState(false)
    const [highlightLimitWarning, setHighlightLimitWarning] = useState(false)
    const [largeIconsNotAllowed, setLargeIconsNotAllowed] = useState(false)
    const largeIconExists = product?.c_engravingLargeIcons
        ? product?.c_engravingLargeIcons?.some((i) => symbolsEngraved.includes(i))
        : false
    // Calculates weighted length for character count
    const getWeightedLength = (str) =>
        Array.from(str).reduce((length, ch) => length + (symbolChars.test(ch) ? 2 : 1), 0)

    const weightedLength = getWeightedLength(cleanedValue)
    const [isError, setIsError] = useState(false)
    const notAllowed = restrictionListArray?.indexOf(cleanedValue) !== -1
    const placeholderText = `Enter up to ${maxLength} characters`
    const charsRemaining = largeIconExists ? 0 : Math.max(0, maxLength - weightedLength)

    const handleInputChange = (e) => {
        const inputVal = isMonogram ? formatMonogramInput(e.target.value) : e.target.value
        const symbolsEngraved = getEngravedSymbols(inputVal)
        const lastChar = inputVal.slice(-1)
        if (lastChar && !allowedCharsRegex.test(lastChar)) {
            if (!isError) {
                setIsError(true)
                setTimeout(() => setIsError(false), 3000)
            }
            return
        }
        const gtmEngravedLine =
            replaceSymbols(
                inputVal
                    ?.replaceAll('{{#font=kendra-characters-24-01-22.ttf}}', '')
                    ?.replaceAll('{{/}}', '')
            ) || ''
        const largeIconExists = product?.c_engravingLargeIcons
            ? product?.c_engravingLargeIcons?.some((i) => symbolsEngraved.includes(i))
            : false
        if (largeIconExists) {
            if (!largeIconsNotAllowed) {
                setLargeIconsNotAllowed(true)
                setTimeout(() => setLargeIconsNotAllowed(false), 3000)
            }
            return
        }
        const isOverLimit =
            getWeightedLength(
                inputVal
                    ?.replaceAll('{{#font=kendra-characters-24-01-22.ttf}}', '')
                    ?.replaceAll('{{/}}', '')
            ) > maxLength

        if (!isOverLimit) {
            handleLineChange(
                inputVal.replace(
                    /[\uE000-\uF8FF]/g,
                    (m) => `{{#font=kendra-characters-24-01-22.ttf}}${m}{{/}}`
                )
            )
        } else {
            if (!highlightLimitWarning) {
                setHighlightLimitWarning(true)
                setTimeout(() => setHighlightLimitWarning(false), 4000)
            }
            return
        }
        tagManager.engravingText(gtmEngravedLine)
    }

    const handleIconSelect = (iconChar, label) => {
        if (
            largeIconExists ||
            (value && product?.c_engravingLargeIcons
                ? product?.c_engravingLargeIcons?.indexOf(label) != -1
                : false)
        ) {
            if (!largeIconsNotAllowed) {
                setLargeIconsNotAllowed(true)
                setTimeout(() => setLargeIconsNotAllowed(false), 3000)
            }
            return
        }
        const currentWeightedLength = getWeightedLength(
            cleanedValue
                ?.replaceAll('{{#font=kendra-characters-24-01-22.ttf}}', '')
                ?.replaceAll('{{/}}', '')
        )
        const isSymbol = symbolChars.test(iconChar)

        // Prevent adding symbol if current length is maxLength - 1
        if (isSymbol && currentWeightedLength === maxLength - 1 && currentWeightedLength !== 0) {
            if (!highlightLimitWarning) {
                setHighlightLimitWarning(true)
                setTimeout(() => setHighlightLimitWarning(false), 3000)
            }
            return
        }

        const newValue = value + iconChar
        if (
            getWeightedLength(
                newValue
                    ?.replaceAll('{{#font=kendra-characters-24-01-22.ttf}}', '')
                    ?.replaceAll('{{/}}', '')
            ) <= maxLength ||
            currentWeightedLength == 0
        ) {
            handleLineChange(newValue)
        } else {
            if (!highlightLimitWarning) {
                setHighlightLimitWarning(true)
                setTimeout(() => setHighlightLimitWarning(false), 3000)
            }
            return
        }
        const gtmEngravedLine =
            replaceSymbols(
                newValue
                    ?.replaceAll('{{#font=kendra-characters-24-01-22.ttf}}', '')
                    ?.replaceAll('{{/}}', '')
            ) || ''
        tagManager.engravingText(gtmEngravedLine)
        const hasZodiac = (l) => getZodiacSigns()?.some((z) => z?.[l])
        if (hasZodiac(label)) {
            tagManager.engravingZodiacSign(label)
        } else {
            tagManager.engravingSymbol(label)
        }
    }

    return (
        <Box className={id} mt={{base: '20px', md: '15px'}} position="relative">
            <FormControl>
                <FormLabel
                    htmlFor={id}
                    key={cleanedValue + isFocused}
                    position="absolute"
                    left="12px"
                    top={isFocused || cleanedValue ? '4px' : '16px'}
                    fontSize={isFocused || cleanedValue ? '13px' : '16px'}
                    color="black.100"
                    fontWeight="300"
                    bg="white"
                    px="4px"
                    transition={{lg: 'top 0.2s ease'}}
                    pointerEvents="none"
                    zIndex="1"
                >
                    {placeholderText}
                </FormLabel>

                <InputGroup>
                    <Input
                        id={id}
                        ref={inputRef}
                        value={cleanedValue}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        onChange={handleInputChange}
                        fontFamily="ksSansSerif"
                        padding="16px 15px 6px"
                        height="56px"
                        fontWeight="300"
                        color="black.100"
                        fontSize="16px"
                        borderColor={isError || notAllowed ? 'red.400' : 'gray.600'}
                        _focusVisible={{borderColor: 'gray.600'}}
                        _focus={{borderColor: 'gray.600'}}
                        _hover={{
                            borderColor: 'var(--chakra-colors-purple-300)',
                            boxShadow:
                                isError || notAllowed
                                    ? '0px 3px var(--chakra-colors-red-400)'
                                    : '0px 3px var(--chakra-colors-purple-300)'
                        }}
                    />
                </InputGroup>
                <Box
                    display="inline-flex"
                    justifyContent="space-between"
                    width="100%"
                    alignItems="baseline"
                >
                    <Box maxWidth={{base: '50%', lg: '70%'}}>
                        {!showSpinner && isError && (
                            <Text color="red.400" mt="10px" fontSize="12px" data-error={isError}>
                                <FormattedMessage
                                    id="engraving.guide.1"
                                    defaultMessage="Please use only letters, numbers & symbols within length."
                                />
                            </Text>
                        )}

                        {!showSpinner && notAllowed && (
                            <Text color="red.400" mt="10px" fontSize="12px" data-error={notAllowed}>
                                <FormattedMessage
                                    id="engraving.not.allowed"
                                    defaultMessage="This engraving is not allowed."
                                />
                            </Text>
                        )}

                        {!showSpinner && largeIconsNotAllowed && (
                            <Text
                                color="red.400"
                                mt="10px"
                                fontSize="12px"
                                data-error={largeIconsNotAllowed}
                            >
                                <FormattedMessage
                                    id="engraved.by.itself"
                                    defaultMessage="This symbol can only be engraved by itself."
                                />
                            </Text>
                        )}

                        <Text
                            mt="5px"
                            key={id + value + charsRemaining}
                            fontSize="13px"
                            fontWeight="300"
                            lineHeight="22px"
                            color={highlightLimitWarning ? 'red.400' : 'black.300'}
                        >
                            {highlightLimitWarning && charsRemaining > 0 ? (
                                <FormattedMessage
                                    id="more.characters.than.available"
                                    defaultMessage="Symbol takes up more characters than available."
                                />
                            ) : isMonogram && charsRemaining === 3 ? (
                                <FormattedMessage
                                    id="engraving.guide.2.monogram"
                                    defaultMessage="{charsRemaining} characters required."
                                    values={{charsRemaining}}
                                />
                            ) : (
                                <FormattedMessage
                                    id="engraving.guide.2"
                                    defaultMessage="{charsRemaining} characters remaining."
                                    values={{charsRemaining}}
                                />
                            )}
                        </Text>
                    </Box>
                    {!isMonogram && (
                        <Box
                            onClick={() => {
                                setShowSpecialIcons((prev) => !prev)
                                setDisplayIconType('')
                                setShowFonts(false)
                                setSelectedHeading(
                                    displayIconType === 'ks' || displayIconType === ''
                                        ? 'symbols'
                                        : 'zodiac'
                                )
                            }}
                            gap="10px"
                            display="flex"
                            alignItems="center"
                            marginLeft="auto"
                            width="fit-content"
                            cursor="pointer"
                            marginTop="10px"
                            marginRight="3px"
                        >
                            <Symbols width="17px" height="17px" color="black.100" alt="symbols" />
                            <Text
                                fontSize="13px"
                                fontWeight="300"
                                lineHeight="22px"
                                color="black.300"
                                textDecoration="underline"
                                textUnderlineOffset="4px"
                            >
                                <FormattedMessage
                                    id="insert.a.symbol"
                                    defaultMessage="Insert a Symbol"
                                />
                            </Text>
                            <Image
                                transform={
                                    showSpecialIcons ||
                                    displayIconType === 'ks' ||
                                    displayIconType === 'zodiac'
                                        ? 'rotate(-90deg)'
                                        : 'rotate(90deg)'
                                }
                                src={getUrl('UIImages/images/redesign/arrow-default.svg')}
                            />
                        </Box>
                    )}
                </Box>
            </FormControl>

            {!isMonogram && (
                <Box
                    className={`special-icons-wrapper ${
                        showSpecialIcons || displayIconType === 'ks' || displayIconType === 'zodiac'
                            ? 'show'
                            : ''
                    }`}
                    mt={
                        showSpecialIcons || displayIconType === 'ks' || displayIconType === 'zodiac'
                            ? '20px'
                            : '0px'
                    }
                    position="relative"
                    borderRadius="3px"
                >
                    <SpecialIcons
                        product={product}
                        displayIconType={displayIconType}
                        symbolsEngraved={symbolsEngraved}
                        setDisplayIconType={setDisplayIconType}
                        onIconSelect={(value, label) => {
                            handleIconSelect(
                                `{{#font=kendra-characters-24-01-22.ttf}}${value}{{/}}`,
                                label
                            )
                        }}
                    />
                </Box>
            )}
        </Box>
    )
}

EngravingInput.propTypes = {
    id: PropTypes.string,
    value: PropTypes.string,
    maxLength: PropTypes.any,
    displayIconType: PropTypes.any,
    setDisplayIconType: PropTypes.any,
    handleLineChange: PropTypes.func,
    restrictionListArray: PropTypes.array,
    showSpinner: PropTypes.any,
    setShowFonts: PropTypes.any,
    selectedFont: PropTypes.any,
    setSelectedHeading: PropTypes.any,
    selectedHeading: PropTypes.any,
    selectedFaceLines: PropTypes.any,
    product: PropTypes.object
}

export default EngravingInput
